import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { BsDownload } from "react-icons/bs";
import InvoicePdfPreview from "../../../components/InvoicePdfPreview";
import InvoicePdfTemplate from "../../../components/InvoicePdfTemplate";
import generatePdfBlob from "../../../components/InvoicePdfExporter";
import { toISODateOnly } from "../../../utils/dateMaker";
import Axios from "../../../utils/Axios";
import {
  PROJECT_DETAILS_PID,
  INVOICE_LIST_BY_PROJECT,
} from "../../../config/API_Collection";
import "../../../styles/InvoiceModal.css";

export default function InvoiceModal({
  open,
  onClose,
  invoice = {},
  projectName = "",
  hideBudgets = false,
}) {
  // Pull the detailed invoice from Redux (populated by INVOICE_DETAIL flow)
  const { selectedInvoice } = useSelector((state) => state.invoices);
  // Try to get current project details (contains client name and budgets)
  const project = useSelector((state) => state?.projectDetails?.project);

  // Prefer selectedInvoice from store; fallback to prop if provided
  const currentInvoice = selectedInvoice || invoice || {};

  // Items come from API as `invoice_items` (array of { invoice_item_id, budget_id, amount, ... })
  const items = Array.isArray(currentInvoice.invoice_items)
    ? currentInvoice.invoice_items
    : Array.isArray(currentInvoice.items)
    ? currentInvoice.items
    : [];

  // Group items by budget_id to render budget blocks similar to NewInvoiceModal
  // const [openBudgets, setOpenBudgets] = useState({});
  const [budgetGroups, setBudgetGroups] = useState([]);
  const [projectData, setProjectData] = useState(null);
  const [fetchedClientName, setFetchedClientName] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const templateRef = useRef(null);

  useEffect(() => {
    const groups = {};
    // Prefer budgets fetched into projectData if present; otherwise fall back to Redux project
    const projBudgetsFromProject =
      project?.budgets || project?.project_data?.budgets || [];
    const projBudgetsFromFetched =
      projectData?.budgets || projectData?.project_data?.budgets || [];
    const projectBudgets =
      Array.isArray(projBudgetsFromFetched) && projBudgetsFromFetched.length > 0
        ? projBudgetsFromFetched
        : projBudgetsFromProject;
    // If invoice items exist, group by their budget_id. Otherwise, fall back to project budgets
    if ((items || []).length > 0) {
      (items || []).forEach((it) => {
        const bid = it.budget_id ?? it.budgetId ?? "none";
        // Find existing project budget to enrich with tasks/name when available
        const foundBudget = projectBudgets.find(
          (pb) => Number(pb.bid ?? pb.id) === Number(bid)
        );
        const budgetName =
          it.budget_name ||
          it.budget ||
          (foundBudget && (foundBudget.budget_name || foundBudget.name)) ||
          `BD${String(bid).padStart(3, "0")}`;
        const budgetTotal = foundBudget
          ? foundBudget.budget ?? foundBudget.amount ?? foundBudget.budget_total
          : undefined;
        if (!groups[bid])
          groups[bid] = {
            bid,
            budget_name: budgetName,
            tasks: [],
            amount: 0,
            budget_total: budgetTotal,
          };
        // If the invoice item has detailed task rows, push them; otherwise, try to use foundBudget.tasks
        if (it.task_id || it.task_name || it.hours_spent || it.cost_spent) {
          groups[bid].tasks.push(it);
        } else if (foundBudget && Array.isArray(foundBudget.tasks)) {
          // include budget tasks for richer view when invoice items only contain totals
          groups[bid].tasks = groups[bid].tasks.concat(foundBudget.tasks);
        }
        const amt = it?.amount ?? it?.total_amount ?? 0;
        const n = typeof amt === "string" ? parseFloat(amt) : Number(amt || 0);
        groups[bid].amount += isNaN(n) ? 0 : n;
      });
    } else if (projectBudgets && projectBudgets.length > 0) {
      // No invoice-specific items: show budgets from projectData so user can see budget tasks
      projectBudgets.forEach((pb) => {
        const bid = pb.bid ?? pb.id ?? "none";
        const budgetName =
          pb.budget_name || pb.name || `BD${String(bid).padStart(3, "0")}`;
        const budgetTotal = pb.budget ?? pb.amount ?? pb.budget_total;
        groups[bid] = {
          bid,
          budget_name: budgetName,
          tasks: Array.isArray(pb.tasks) ? pb.tasks : [],
          amount: 0,
          budget_total: budgetTotal,
        };
      });
    }
    const arr = Object.values(groups);
    // Only update state if the computed groups/open state actually changed
    setBudgetGroups((prev) => {
      try {
        const same = JSON.stringify(prev) === JSON.stringify(arr);
        return same ? prev : arr;
      } catch (e) {
        return arr;
      }
    });
    const initialOpen = {};
    arr.forEach((g) => {
      initialOpen[g.bid] = true;
    });
    // setOpenBudgets((prev) => {
    //   try {
    //     const same = JSON.stringify(prev) === JSON.stringify(initialOpen);
    //     return same ? prev : initialOpen;
    //   } catch (e) {
    //     return initialOpen;
    //   }
    // });
  }, [items, project, projectData]);

  // const toggleBudget = (bid) => setOpenBudgets(p => ({ ...p, [bid]: !p[bid] }));

  const computedSubTotal = items.reduce((sum, it) => {
    const amt = it?.amount ?? it?.total_amount ?? 0;
    const n = typeof amt === "string" ? parseFloat(amt) : Number(amt || 0);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  const gstRate =
    currentInvoice.gst !== undefined && currentInvoice.gst !== null
      ? Number(currentInvoice.gst)
      : 0.15; // fallback only for rate, not for data display

  const hasItems = items.length > 0;
  const parseNumber = (v) => {
    const n = typeof v === "string" ? parseFloat(v) : Number(v || 0);
    return isNaN(n) ? 0 : n;
  };

  const subTotal = hasItems
    ? computedSubTotal
    : parseNumber(currentInvoice.sub_total);
  const total = hasItems
    ? subTotal * (1 + (isNaN(gstRate) ? 0 : gstRate))
    : parseNumber(currentInvoice.total || 0);

  // Fetch project details if not available in Redux and we have a project id
  useEffect(() => {
    const pid =
      currentInvoice.project_id ||
      currentInvoice.projectId ||
      currentInvoice.projectId;
    if (!project && pid) {
      let isMounted = true;
      const fetchProject = async () => {
        try {
          const resp = await Axios.get(`${PROJECT_DETAILS_PID}${pid}`);
          if (isMounted) {
            const newData = resp.data?.data || resp.data;
            setProjectData((prev) => {
              try {
                const same = JSON.stringify(prev) === JSON.stringify(newData);
                return same ? prev : newData;
              } catch (e) {
                return newData;
              }
            });
          }
        } catch (e) {
          console.debug(
            "Could not fetch project details for invoice view",
            e?.message || e
          );
        }
      };
      fetchProject();
      return () => {
        isMounted = false;
      };
    }
  }, [project, currentInvoice.project_id]);

  // Fallback: if invoice / project don't include client_name, try invoices-by-project endpoint
  useEffect(() => {
    const existingName =
      currentInvoice.client_name ||
      currentInvoice.client ||
      project?.client_name ||
      projectData?.client_name;
    if (existingName) {
      // Clear fetched client name only if it exists to avoid unnecessary state updates
      if (fetchedClientName) setFetchedClientName("");
      return;
    }

    const pid =
      currentInvoice.project_id ||
      currentInvoice.projectId ||
      currentInvoice.projectId;
    if (!pid) return;

    let isMounted = true;
    const fetchClientName = async () => {
      try {
        // Use invoices-by-project endpoint to get client_name (avoid duplicate invoice_detail call)
        const resp = await Axios.get(
          `${INVOICE_LIST_BY_PROJECT}?project_id=${pid}`
        );
        const name = resp?.data?.data?.client_name || resp?.data?.client_name;
        if (isMounted && name && name !== fetchedClientName)
          setFetchedClientName(name);
      } catch (err) {
        console.debug(
          "Could not fetch client name from invoices-by-project",
          err?.message || err
        );
      }
    };
    fetchClientName();
    return () => {
      isMounted = false;
    };
  }, [
    currentInvoice.project_id,
    currentInvoice.projectId,
    project,
    projectData,
    currentInvoice.client_name,
    fetchedClientName,
  ]);

  // Avoid rendering when modal isn't open, but declare all hooks above first
  if (!open) return null;

  const invoiceNo = currentInvoice.xero_no || "INV-XXXX";

  const handleDirectDownload = async () => {
    setDownloadLoading(true);
    try {
      const id = templateRef.current?.id || "invoice-template-hidden";
      const blob = await generatePdfBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const invoiceTitle = currentInvoice.invoice_title || "";
      const fileName = invoiceTitle
        ? `Invoice ${invoiceNo} - ${invoiceTitle}.pdf`
        : `Invoice ${invoiceNo}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation failed", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="new-invoice-dialog"
      contentClassName="new-invoice-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="mb-0">
          {invoiceNo} - {currentInvoice.invoice_title || projectName || ""}
        </Modal.Title>
        {currentInvoice.invoice_status === 2 && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm ms-2"
            title="Download PDF"
            onClick={handleDirectDownload}
            disabled={downloadLoading}
            style={{ marginLeft: "8px" }}
          >
            {downloadLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <BsDownload />
            )}
          </button>
        )}
      </Modal.Header>
      <Modal.Body>
        <div id="invoice-pdf-root">
          <div className="invoice-top-grid">
            <div className="grid-left">
              <div className="grid-left-top">
                <div>
                  <label className="form-label">Invoice To</label>
                  <input
                    className="form-control"
                    value={
                      currentInvoice.client_name || fetchedClientName || ""
                    }
                    readOnly
                  />
                </div>
                <div>
                  <label className="form-label">Billing Contact</label>
                  <input
                    className="form-control"
                    value={currentInvoice.billing_contact || ""}
                    readOnly
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Invoice Title</label>

                <input
                  className="form-control"
                  value={currentInvoice.invoice_title || ""}
                  readOnly
                />
              </div>
              <div className="mt-2">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={currentInvoice.description || ""}
                  readOnly
                />
              </div>
            </div>

            <div className="grid-right">
              <div className="top-right-grid">
                <div>
                  <label className="form-label">Invoice Date</label>
                  <input
                    className="form-control"
                    value={toISODateOnly(
                      currentInvoice.invoice_date || currentInvoice.invoiceDate
                    )}
                    readOnly
                  />
                </div>
                <div>
                  <label className="form-label">Due Date</label>
                  <input
                    className="form-control"
                    value={toISODateOnly(
                      currentInvoice.due_date || currentInvoice.dueDate
                    )}
                    readOnly
                  />
                </div>
                <div>
                  <label className="form-label">GST(%)</label>
                  <input
                    className="form-control"
                    value={`${(Number(currentInvoice.gst) || 0) * 100}%`}
                    readOnly
                  />
                </div>
                <div>
                  <label className="form-label">Reference</label>
                  <input
                    className="form-control"
                    value={currentInvoice.reference || ""}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Budgets accordion similar to NewInvoiceModal but read-only */}
          <div className="budget-list-box">
            {!hideBudgets &&
              (budgetGroups.length > 0 ? (
                budgetGroups.map((b) => (
                  <div key={b.bid} className="mb-2 budget-block">
                    <div className="budget-header d-flex align-items-center justify-content-between p-2">
                      <div className="d-flex align-items-center gap-2">
                        {/* <button type="button" className="btn btn-link p-0 budget-toggle" onClick={() => toggleBudget(b.bid)} aria-label="toggle">
                      {openBudgets[b.bid] ? <BsChevronDown size={16} /> : <BsChevronRight size={16} />}
                    </button> */}
                        <span className="text-muted">
                          BD{String(b.bid).padStart(3, "0")} - {b.budget_name}
                        </span>
                      </div>
                      <div style={{ width: 140 }}>
                        <input
                          className="form-control text-end"
                          value={`$${(isNaN(Number(b.amount))
                            ? 0
                            : Number(b.amount)
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Budget ID</th>
                        <th>Description</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const amt = item?.amount ?? item?.total_amount ?? 0;
                        const n =
                          typeof amt === "string"
                            ? parseFloat(amt)
                            : Number(amt || 0);
                        return (
                          <tr key={item.invoice_item_id || idx}>
                            <td>{item.budget_id ?? ""}</td>
                            <td>{item.description ?? ""}</td>
                            <td className="text-end">
                              $
                              {(isNaN(n) ? 0 : n).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
          </div>

          <div className="mt-3 d-flex justify-content-between align-items-start">
            {/* Left Column - Notes */}
            <div className="notes-box" style={{ width: "48%" }}>
              <div className="paid-seal">
                {currentInvoice.xero_status === 1 && (
                  <img
                    src="/assets/images/paid.png"
                    alt="Paid Seal"
                    width={150}
                  />
                )}
              </div>
            </div>

            {/* Right Column - Totals */}
            <div className="totals-box" style={{ width: "48%" }}>
              <div className="mt-3 d-flex justify-content-end totals-summary">
                <div className="totals-box">
                  <div className="d-flex justify-content-between totals-line">
                    <span className="totals-label">Sub Total</span>
                    <span className="totals-value">
                      $
                      {Number(subTotal).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between totals-line">
                    <span className="totals-label">
                      GST {(parseFloat(gstRate) || 0) * 100}%
                    </span>
                    <span className="totals-value">
                      $
                      {(
                        (isNaN(subTotal) ? 0 : subTotal) *
                        (isNaN(gstRate) ? 0 : gstRate)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between fw-bold">
                    <span>TOTAL (NZD)</span>
                    <span>
                      $
                      {Number(total).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden PDF template for direct download */}
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <div ref={templateRef} id="invoice-template-hidden-wrap">
            <InvoicePdfTemplate
              id="invoice-template-hidden"
              invoiceNo={invoiceNo || "-"}
              invoiceDate={
                currentInvoice.invoice_date || currentInvoice.date || "-"
              }
              subtotal={subTotal || 0}
              gstAmount={subTotal * gstRate || 0}
              total={total || 0}
              dueDate={currentInvoice.due_date || currentInvoice.dueDate || "-"}
              clientName={
                currentInvoice.client_name || fetchedClientName || ""
              }
              contactName={currentInvoice.billing_contact || ""}
              clientAddress={
                currentInvoice.client_address ||
                project?.client_address ||
                project?.project_data?.client_address ||
                projectData?.client_address ||
                projectData?.project_data?.client_address ||
                ""
              }
              invoiceTitle={currentInvoice.invoice_title || ""}
              description={currentInvoice.description || ""}
              reference={currentInvoice.reference || ""}
              
            />
          </div>
        </div>

        <InvoicePdfPreview
          show={previewOpen}
          onClose={() => setPreviewOpen(false)}
          elementId="invoice-pdf-root"
          invoiceNo={invoiceNo}
          invoiceTitle={currentInvoice.invoice_title || ""}
          useTemplate={true}
          invoiceData={{
            invoiceNo: invoiceNo || "-",
            invoiceDate:
              currentInvoice.invoice_date || currentInvoice.date || "-",
            subtotal: subTotal || 0,
            gstAmount: subTotal * gstRate || 0,
            total: total || 0,
            dueDate: currentInvoice.due_date || currentInvoice.dueDate || "-",
            clientName:
              currentInvoice.client_name ||
              fetchedClientName ||
              "-",
            contactName:
              currentInvoice.billing_contact ||
              "-",
            clientAddress:
              currentInvoice.client_address ||
              project?.client_address ||
              project?.project_data?.client_address ||
              projectData?.client_address ||
              projectData?.project_data?.client_address ||
              "-",
            invoiceTitle:
              currentInvoice.invoice_title ||
              "-",
            description: currentInvoice.description || "Description",
            reference: currentInvoice.reference || "-"
          }}
        />
      </Modal.Body>
    </Modal>
  );
}
